/**
 * The fill-ins an automation email carries, and the one filler that fills them.
 *
 * Pure: no worker imports, so the sender in the Worker and the preview in the
 * editor render the same words from the same code. One catalogue says what a
 * fill-in is called and what it means, so the producer that writes emails, the
 * validator that reads them, the editor that offers them and the sender that
 * fills them can never disagree.
 */
import { DEFAULT_ACCENT, emailPalette, inkOn } from "./brand-colors";
import { scheduleZone, wallClock } from "./live-time";
import { isCustomLook, readLook } from "./offer";
import type { OfferJson, ScheduleJson } from "./types";

// ── the catalogue ───────────────────────────────────────────────────────
export type TokenGroup =
  | "The person"
  | "The room"
  | "The workspace"
  | "Links"
  | "The offer";

export type TokenKey =
  | "first name"
  | "last name"
  | "email"
  | "plan"
  | "webinar title"
  | "host name"
  | "starts at"
  | "workspace name"
  | "private page"
  | "watch page"
  | "video"
  | "unsubscribe"
  | "offer"
  | "offer link"
  | "offer words"
  | "offer code";

export interface TokenEntry {
  /** How it is written in an email, exactly. */
  token: string;
  key: TokenKey;
  /** One line the host reads in the "Insert a fill-in" list. */
  meaning: string;
  group: TokenGroup;
}

/** Every fill-in, in the order the editor lists them. */
export const TOKENS: readonly TokenEntry[] = [
  { token: "{{first name}}", key: "first name", meaning: "Their first name", group: "The person" },
  { token: "{{last name}}", key: "last name", meaning: "Their last name", group: "The person" },
  { token: "{{email}}", key: "email", meaning: "The address this is going to", group: "The person" },
  { token: "{{room title}}", key: "webinar title", meaning: "The name of the talk", group: "The room" },
  { token: "{{host name}}", key: "host name", meaning: "Who is hosting it", group: "The room" },
  {
    token: "{{starts at}}",
    key: "starts at",
    meaning: "The date and time of the session, empty on a talk people watch any time",
    group: "The room",
  },
  { token: "{{workspace name}}", key: "workspace name", meaning: "Your workspace name", group: "The workspace" },
  { token: "{{plan}}", key: "plan", meaning: "The plan they bought, or \"your plan\"", group: "The person" },
  { token: "{{private page}}", key: "private page", meaning: "A link to their own page", group: "Links" },
  { token: "{{watch page}}", key: "watch page", meaning: "A link to watch the talk", group: "Links" },
  {
    token: "{{their video}}",
    key: "video",
    meaning: "A link to their own video, when the app that sent them gave one",
    group: "Links",
  },
  { token: "{{unsubscribe}}", key: "unsubscribe", meaning: "Their own opt-out link", group: "Links" },
  { token: "{{offer}}", key: "offer", meaning: "The offer from the talk, button and all", group: "The offer" },
  { token: "{{offer link}}", key: "offer link", meaning: "Where the offer goes, on its own", group: "The offer" },
  { token: "{{offer words}}", key: "offer words", meaning: "What the offer says, in your own words", group: "The offer" },
  { token: "{{offer code}}", key: "offer code", meaning: "The code, on its own", group: "The offer" },
];

const KEYS: readonly string[] = TOKENS.map((t) => t.key);

/** The groups in the order the editor draws them. */
export const TOKEN_GROUPS: readonly TokenGroup[] = [
  "The person",
  "The room",
  "The workspace",
  "Links",
  "The offer",
];

/** Anything written as `{{ ... }}`, whether or not we know it. */
const TOKEN_RE = /\{\{([^}]*)\}\}/g;

/**
 * The catalogue name a written fill-in means, or null. Spacing, underscores and
 * case are all forgiven, and the two older spellings still work: `{{name}}` is
 * the first name and `{{private link}}` is the private page.
 */
export function tokenKey(inner: string): TokenKey | null {
  const k = inner.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (k === "name") return "first name";
  if (k === "private link") return "private page";
  const word = FILL_WORDS[k];
  if (word) return word;
  return KEYS.includes(k) ? (k as TokenKey) : null;
}

/**
 * The words the editor's fill-ins are written in, for the catalogue's keys. A host reads "room title", not
 * "webinar title", and every earlier spelling still fills the same way.
 */
const FILL_WORDS: Readonly<Record<string, TokenKey>> = {
  "room title": "webinar title",
  host: "host name",
  business: "workspace name",
  "session time": "starts at",
  "their page": "private page",
  "their video": "video",
};

/** The fill-ins the editor offers, in the order it shows them, as the host reads them. */
export const FILL_INS: ReadonlyArray<{ token: string; key: TokenKey }> = [
  { token: "{{first name}}", key: "first name" },
  { token: "{{last name}}", key: "last name" },
  { token: "{{room title}}", key: "webinar title" },
  { token: "{{host}}", key: "host name" },
  { token: "{{business}}", key: "workspace name" },
  { token: "{{session time}}", key: "starts at" },
  { token: "{{their page}}", key: "private page" },
  { token: "{{their video}}", key: "video" },
  { token: "{{watch page}}", key: "watch page" },
  { token: "{{offer link}}", key: "offer link" },
  { token: "{{offer words}}", key: "offer words" },
  { token: "{{unsubscribe}}", key: "unsubscribe" },
];

/** A written body with every fill-in in the words the editor uses, so an older step reads like a new one. */
export function friendlyTokens(text: string): string {
  return text.replace(TOKEN_RE, (whole, inner: string) => {
    const key = tokenKey(inner);
    const fill = key ? FILL_INS.find((f) => f.key === key) : undefined;
    return fill ? fill.token : whole;
  });
}

/** Is every `{{...}}` in the text one of the fill-ins the sender knows? */
export function tokensOk(text: string): boolean {
  const found = text.match(TOKEN_RE) ?? [];
  return found.every((p) => tokenKey(p.slice(2, -2)) !== null);
}

// ── the two guarantees the producer's emails carry ──────────────────────
export const PAGE_TOKEN = /\{\{\s*(?:private[ _]?(?:page|link)|their[ _]?page)\s*\}\}/i;
export const PAGE_TOKENS = new RegExp(PAGE_TOKEN.source, "gi");
export const NAME_TOKENS = /\{\{\s*(?:first[ _]?name|name)\s*\}\}/gi;
export const OFFER_TOKEN = /\{\{\s*offer\s*\}\}/i;
const WATCH_TOKEN = /\{\{\s*watch[ _]?page\s*\}\}/i;

export function hasPageToken(text: string): boolean {
  return PAGE_TOKEN.test(text);
}

export function hasWatchToken(text: string): boolean {
  return WATCH_TOKEN.test(text);
}

/** The body with a link to where they watch in it: as written (either page), or the Room on its own line at the end. */
export function withPageToken(body: string): string {
  return hasPageToken(body) || hasWatchToken(body) ? body : `${body.trimEnd()}\n\n{{private page}}`;
}

export function hasOfferToken(text: string): boolean {
  return OFFER_TOKEN.test(text);
}

/** The body with the offer in it: as written, or on its own line before the page link. */
export function withOfferToken(body: string): string {
  if (hasOfferToken(body)) return body;
  const at = body.search(PAGE_TOKEN);
  if (at < 0) return `${body.trimEnd()}\n\n{{offer}}`;
  const head = body.slice(0, at).trimEnd();
  return `${head}\n\n{{offer}}\n\n${body.slice(at).trimStart()}`;
}

// ── what a fill-in is filled with ───────────────────────────────────────
/** The label an automation email puts on the reader's own page. */
export const PRIVATE_PAGE_LABEL = "Your private page";
/** The label an email puts on the page where the talk plays. */
export const WATCH_PAGE_LABEL = "Watch the talk";
/** The words a bare unsubscribe fill-in becomes when the host wraps nothing round it. */
const UNSUBSCRIBE_LABEL = "Unsubscribe";
/** The words an email puts on a person's own video link. */
export const VIDEO_LABEL = "Watch your video";

export interface TokenValues {
  firstName?: string;
  lastName?: string;
  email?: string;
  webinarTitle?: string;
  hostName?: string;
  /** Already written out, e.g. "Tue 2 Sep, 10:00 (London)". Empty on demand. */
  startsAt?: string;
  workspaceName?: string;
  /**
   * The plan the person is on, written on their contact by whatever sold it.
   * Empty falls back to "your plan", because an empty first sentence is the
   * worst place for a blank.
   */
  plan?: string | null;
  /** The reader's own page, or null when this run has none. */
  privatePage?: string | null;
  /** The page the talk plays on, or null when this run has none. */
  watchPage?: string | null;
  /** This person's own video, written on their contact by the app that sent them. Null when none. */
  video?: string | null;
  /**
   * This reader's own opt-out address. Different for every person, so it can
   * only be filled in at send time, never typed by the host.
   */
  unsubscribe?: string | null;
  /** The offer as the card has it right now, read fresh at send time. */
  offer?: OfferJson | null;
  /**
   * Where the offer button goes. The email passes the tracked address so a press
   * counts; a preview passes the card's own. Empty falls back to the card's url.
   */
  offerLink?: string | null;
  /**
   * The workspace's brand colours, which the offer button takes when the offer
   * box is in the page's own colours. Absent means Flashroom's own.
   */
  brand?: { accent?: string | null; brass?: string | null } | null;
}

/** A full name split the way the fill-ins need it. */
export function namesFrom(fullName: string): { firstName: string; lastName: string } {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Where a code is used, in the words a person reads: the site's own name. */
function siteOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/** Where the offer's button points, for the kinds that have one. */
function offerLinkOf(v: TokenValues): string {
  const offer = v.offer;
  if (!offer) return "";
  if (offer.kind === "code" || offer.kind === "message") return "";
  return (v.offerLink ?? "").trim() || (offer.url ?? "").trim();
}

/** The offer in plain text, kind by kind. */
function offerAsText(v: TokenValues): string {
  const offer = v.offer;
  if (!offer) return "";
  switch (offer.kind) {
    case "call":
    case "buy": {
      const url = offerLinkOf(v);
      return url ? `Get started: ${url}` : "";
    }
    case "code":
      return offer.code ? `Your code: ${offer.code}` : "";
    case "download":
      return offerLinkOf(v);
    default:
      return (offer.text ?? "").trim();
  }
}

/**
 * The offer button's fill in an email: the colour of the offer box the reader
 * saw on the page, so the page and the inbox promise the same thing. Wine and
 * ink are their own colours, a colour the host picked is that colour, and the
 * page's own look (paper, brand, or none) is the workspace accent.
 */
function offerFill(v: TokenValues): string {
  const look = readLook(v.offer?.look);
  if (look === "wine") return DEFAULT_ACCENT;
  if (look === "ink") return "#100c0e";
  if (isCustomLook(look)) return look;
  return emailPalette(v.brand).button;
}

/** The look of the offer button inside an email body, with text that reads on it. */
function offerButtonStyle(v: TokenValues): string {
  const fill = offerFill(v);
  return `display:inline-block;padding:11px 20px;border-radius:8px;background:${fill};color:${inkOn(fill)};font-weight:600;text-decoration:none`;
}

function offerCodeStyle(v: TokenValues): string {
  return `display:inline-block;font-size:24px;font-weight:700;letter-spacing:.08em;padding:8px 14px;border:1px dashed ${emailPalette(v.brand).secondary};border-radius:8px`;
}

/** The offer as HTML, kind by kind. */
function offerAsHtml(v: TokenValues): string {
  const offer = v.offer;
  if (!offer) return "";
  const link = offerLinkOf(v);
  switch (offer.kind) {
    case "call":
    case "buy":
      return link
        ? `<a href="${escapeHtml(link)}" style="${offerButtonStyle(v)}">${escapeHtml(offer.label || "Get started")}</a>`
        : "";
    case "code": {
      if (!offer.code) return "";
      const site = offer.url ? siteOf(offer.url) : "";
      const under = site ? `<br>Use it at ${escapeHtml(site)}.` : "";
      return `<span style="${offerCodeStyle(v)}">${escapeHtml(offer.code)}</span>${under}`;
    }
    case "download":
      return link
        ? `<a href="${escapeHtml(link)}" style="${offerButtonStyle(v)}">${escapeHtml(offer.label || "Get the file")}</a>`
        : "";
    default:
      return escapeHtml((offer.text ?? "").trim());
  }
}

function plainFor(key: TokenKey, v: TokenValues): string {
  switch (key) {
    case "first name":
      return (v.firstName ?? "").trim() || "there";
    case "last name":
      return (v.lastName ?? "").trim();
    case "email":
      return (v.email ?? "").trim();
    case "webinar title":
      return (v.webinarTitle ?? "").trim();
    case "host name":
      return (v.hostName ?? "").trim();
    case "starts at":
      return (v.startsAt ?? "").trim();
    case "plan":
      return (v.plan ?? "").trim() || "your plan";
    case "workspace name":
      return (v.workspaceName ?? "").trim();
    case "private page":
      return (v.privatePage ?? "").trim();
    case "unsubscribe":
      return (v.unsubscribe ?? "").trim();
    case "watch page":
      return (v.watchPage ?? "").trim();
    case "video":
      return (v.video ?? "").trim();
    case "offer":
      return offerAsText(v);
    case "offer link":
      return offerLinkOf(v);
    case "offer words":
      return (v.offer?.text ?? "").trim();
    case "offer code":
      return v.offer?.kind === "code" ? (v.offer.code ?? "").trim() : "";
  }
}

function htmlFor(key: TokenKey, v: TokenValues): string {
  if (key === "private page") {
    const page = (v.privatePage ?? "").trim();
    return page ? `<a href="${escapeHtml(page)}">${PRIVATE_PAGE_LABEL}</a>` : "";
  }
  if (key === "watch page") {
    const page = (v.watchPage ?? "").trim();
    return page ? `<a href="${escapeHtml(page)}">${WATCH_PAGE_LABEL}</a>` : "";
  }
  if (key === "video") {
    const url = (v.video ?? "").trim();
    return url ? `<a href="${escapeHtml(url)}">${VIDEO_LABEL}</a>` : "";
  }
  if (key === "unsubscribe") {
    const url = (v.unsubscribe ?? "").trim();
    return url ? `<a href="${escapeHtml(url)}">${UNSUBSCRIBE_LABEL}</a>` : "";
  }
  if (key === "offer") return offerAsHtml(v);
  if (key === "offer link") {
    const url = offerLinkOf(v);
    return url ? `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>` : "";
  }
  return escapeHtml(plainFor(key, v));
}

/**
 * Fill a body the host wrote in the rich box. The words are already html, so
 * nothing here escapes them; only the fill-ins are swapped, each for the html
 * that fill-in means (a link, the offer button, or escaped words).
 */
export function fillRichHtml(html: string, v: TokenValues): string {
  return html.replace(TOKEN_RE, (whole, inner: string) => {
    const key = tokenKey(inner);
    return key ? htmlFor(key, v) : whole;
  });
}

/**
 * The plain-text twin of a rich body. Every email sends both, and a reader whose
 * mail app prefers text must still get the addresses, so a link becomes its words
 * followed by where it goes.
 */
export function richToText(html: string): string {
  const withLinks = html.replace(
    /<a\b[^>]*href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_m, href: string, inner: string) => {
      const words = inner.replace(/<[^>]+>/g, "").trim();
      const url = href.trim();
      if (!url) return words;
      return words && words !== url ? `${words}: ${url}` : url;
    },
  );
  const stripped = withLinks
    .replace(/<li\b[^>]*>/gi, "\n- ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/li>/gi, "")
    .replace(/<\/(p|div|ul|ol|h[1-6])>/gi, "\n\n")
    .replace(/<[^>]+>/g, "");
  const unescaped = stripped
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, "&");
  return unescaped
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Fill the plain-text version: every known fill-in becomes its words. */
export function fillText(text: string, v: TokenValues): string {
  return text.replace(TOKEN_RE, (whole, inner: string) => {
    const key = tokenKey(inner);
    return key ? plainFor(key, v) : whole;
  });
}

/**
 * The stand-in a fill-in leaves behind while the words around it are escaped.
 * A private-use character, so nothing a host can type ever collides with it.
 */
const MARK_OPEN = "\ue000";
const MARK_CLOSE = "\ue001";

/**
 * Fill the HTML body: blank line = paragraph. The links and the offer go in
 * AFTER escaping, so the words can never inject markup and an address is never
 * escaped away.
 */
export function fillHtml(text: string, v: TokenValues): string {
  const parts: string[] = [];
  const marked = text.replace(TOKEN_RE, (whole, inner: string) => {
    const key = tokenKey(inner);
    if (!key) return whole;
    parts.push(htmlFor(key, v));
    return `${MARK_OPEN}${parts.length - 1}${MARK_CLOSE}`;
  });
  const paras = marked
    .split(/\n{2,}/)
    .map(
      (p) =>
        `<p>${escapeHtml(p)
          .replace(/\n/g, "<br>")
          .replace(/\ue000(\d+)\ue001/g, (_m, i: string) => parts[Number(i)] ?? "")}</p>`,
    )
    .join("\n");
  return paras || "<p></p>";
}

// ── "{{starts at}}" ─────────────────────────────────────────────────────
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * The zone in the words a reader wants: "Europe/London" reads "London", and a
 * label that already carries its own bracket ("Eastern Time (ET)") loses it.
 */
function zoneWords(timezone: string): string {
  const raw = timezone.trim();
  if (!raw) return "";
  const tail = raw.includes("/") ? raw.split("/").pop() ?? raw : raw;
  return tail.replace(/_/g, " ").replace(/\s*\([^)]*\)\s*$/, "").trim();
}

/**
 * The wall clock `at` means in the session's own zone. A stored instant (a `Z` or
 * an offset on the end) is read on that zone's clock; a zone-less `at` is already
 * the wall time the host typed, so it is read as written.
 */
function wallTimeOf(at: string, timezone: string): string {
  const zone = scheduleZone(timezone);
  if (!zone || !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(at)) return at;
  const wall = wallClock(at, zone);
  return wall ? `${wall.date}T${wall.time}` : at;
}

/**
 * The session's date and time on the host's clock: "Tue 2 Sep, 10:00 (London)".
 * Empty for a talk with no session (people watch it any time).
 */
export function startsAtLabel(schedule: ScheduleJson | null | undefined): string {
  const at = schedule?.at?.trim();
  if (!at) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(wallTimeOf(at, schedule?.timezone ?? ""));
  if (!m) return "";
  const [, y, mo, d, hh, mm] = m;
  const day = DAYS[new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d))).getUTCDay()];
  const month = MONTHS[Number(mo) - 1];
  if (!day || !month) return "";
  const zone = zoneWords(schedule?.timezone ?? "");
  return `${day} ${Number(d)} ${month}, ${hh}:${mm}${zone ? ` (${zone})` : ""}`;
}
